import {
  getLeoUuid,
  LeoUUID,
  getList,
  getObject,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { QuestionOrder, QuestionOrderDTO } from "./questionOrder";
import { SurveyDetailsAndQuestions, SurveyDetailsAndQuestionsDTO } from "../types/surveyDetailsAndQuestions";
/* eslint-disable import/export */
export abstract class ManageSurveyQuestionsRPC implements
  LeoRPC<ManageSurveyQuestionsRPC.Request, ManageSurveyQuestionsRPC.Response, ManageSurveyQuestionsRPC.Errors.Errors>
{
  abstract execute(
    request: ManageSurveyQuestionsRPC.Request
  ): Promise<LeoRPCResult<ManageSurveyQuestionsRPC.Response, ManageSurveyQuestionsRPC.Errors.Errors>>;
}

export namespace ManageSurveyQuestionsRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    surveyId: string;
    projectId: string;
    questionOrders: QuestionOrderDTO[];
  }
  
  export class Request implements LeoRPCRequest {
    readonly isManageSurveyQuestionsRPCRequest: boolean = true;
  
    readonly surveyId: LeoUUID;
    readonly projectId: LeoUUID;
    readonly questionOrders: QuestionOrder[];
  
    static readonly constraints = {
      "questionOrders": {
        "minSize": 1,
      }
    }
  
    constructor(
    surveyId: LeoUUID, 
    projectId: LeoUUID, 
    questionOrders: QuestionOrder[]
    ) {
      this.surveyId = surveyId;
      this.projectId = projectId;
      Request.validateQuestionOrders(questionOrders);
      this.questionOrders = questionOrders;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const surveyId = getLeoUuid(dto, "surveyId", InvalidRequestSurveyIdError);
      const projectId = getLeoUuid(dto, "projectId", InvalidRequestProjectIdError);
      const questionOrders = getList<QuestionOrderDTO, QuestionOrder>(
        dto,
        "questionOrders",
        QuestionOrder.fromDTO,
        InvalidRequestQuestionOrdersError
      );
      return new Request(
        surveyId,
        projectId,
        questionOrders
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          surveyId: this.surveyId.toDTO(),
          projectId: this.projectId.toDTO(),
          questionOrders: this.questionOrders.map((e: QuestionOrder) => { return e.toDTO() })
      };
      return dto;
    }
    private static validateQuestionOrders = (questionOrders: QuestionOrder[]): void => {
      if(questionOrders.length < 1) {
        throw new InvalidRequestQuestionOrdersMinSizeError(
          1,
          `Attribute questionOrders is too short. Size is ${questionOrders.length}. Min size is 1.`
        );
      }
    }
    
  }
  
  Request.prototype.toString = function toString(): string {
    return `ManageSurveyQuestionsRPC(surveyId='${this.surveyId}',projectId='${this.projectId}',questionOrders='${this.questionOrders}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {
    surveyDetailsAndQuestions: SurveyDetailsAndQuestionsDTO;
  }
  
  export class Response implements LeoRPCResponse {
    readonly isManageSurveyQuestionsResponse: boolean = true;
  
    readonly surveyDetailsAndQuestions: SurveyDetailsAndQuestions;
  
  
    constructor(
    surveyDetailsAndQuestions: SurveyDetailsAndQuestions
    ) {
      this.surveyDetailsAndQuestions = surveyDetailsAndQuestions;
    }
  
    static fromDTO(dto: ResponseDTO): Response {
      const surveyDetailsAndQuestions = SurveyDetailsAndQuestions.fromDTO(getObject(dto, "surveyDetailsAndQuestions", InvalidResponseSurveyDetailsAndQuestionsError));
      return new Response(
        surveyDetailsAndQuestions
      );
    }
  
    toDTO(): ResponseDTO {
      const dto: ResponseDTO = {
          surveyDetailsAndQuestions: this.surveyDetailsAndQuestions.toDTO()
      };
      return dto;
    }
    }
  
  Response.prototype.toString = function toString(): string {
    return `ManageSurveyQuestions(surveyDetailsAndQuestions='${this.surveyDetailsAndQuestions}')`;
  };
  

  export enum RPCError {
    InvalidSurveyId = "INVALID_SURVEY_ID",
    InvalidProjectId = "INVALID_PROJECT_ID",
    SurveyIsClosed = "SURVEY_IS_CLOSED",
    ProjectAlreadyArchived = "PROJECT_ALREADY_ARCHIVED",
    SurveyIsLive = "SURVEY_IS_LIVE",
    OutdatedQuestionsFound = "OUTDATED_QUESTIONS_FOUND",
    InvalidQuestionOrders = "INVALID_QUESTION_ORDERS",
    InvalidRulesFound = "INVALID_RULES_FOUND"
  }

  export namespace Errors {
    
    export interface InvalidSurveyIdDTO {
      code: string;
    }
    
    export class InvalidSurveyId extends LeoRPCError<RPCError> {
      readonly isInvalidSurveyId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidSurveyId;
        super(code);
      }
    
    
      toDTO(): InvalidSurveyIdDTO {
        const dto: InvalidSurveyIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidSurveyId.prototype.toString = function toString(): string {
      return `ManageSurveyQuestionsRPC.INVALID_SURVEY_ID()`;
    };
    
    
    export interface InvalidProjectIdDTO {
      code: string;
    }
    
    export class InvalidProjectId extends LeoRPCError<RPCError> {
      readonly isInvalidProjectId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectId;
        super(code);
      }
    
    
      toDTO(): InvalidProjectIdDTO {
        const dto: InvalidProjectIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectId.prototype.toString = function toString(): string {
      return `ManageSurveyQuestionsRPC.INVALID_PROJECT_ID()`;
    };
    
    
    export interface SurveyIsClosedDTO {
      code: string;
    }
    
    export class SurveyIsClosed extends LeoRPCError<RPCError> {
      readonly isSurveyIsClosed: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.SurveyIsClosed;
        super(code);
      }
    
    
      toDTO(): SurveyIsClosedDTO {
        const dto: SurveyIsClosedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    SurveyIsClosed.prototype.toString = function toString(): string {
      return `ManageSurveyQuestionsRPC.SURVEY_IS_CLOSED()`;
    };
    
    
    export interface ProjectAlreadyArchivedDTO {
      code: string;
    }
    
    export class ProjectAlreadyArchived extends LeoRPCError<RPCError> {
      readonly isProjectAlreadyArchived: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.ProjectAlreadyArchived;
        super(code);
      }
    
    
      toDTO(): ProjectAlreadyArchivedDTO {
        const dto: ProjectAlreadyArchivedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    ProjectAlreadyArchived.prototype.toString = function toString(): string {
      return `ManageSurveyQuestionsRPC.PROJECT_ALREADY_ARCHIVED()`;
    };
    
    
    export interface SurveyIsLiveDTO {
      code: string;
    }
    
    export class SurveyIsLive extends LeoRPCError<RPCError> {
      readonly isSurveyIsLive: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.SurveyIsLive;
        super(code);
      }
    
    
      toDTO(): SurveyIsLiveDTO {
        const dto: SurveyIsLiveDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    SurveyIsLive.prototype.toString = function toString(): string {
      return `ManageSurveyQuestionsRPC.SURVEY_IS_LIVE()`;
    };
    
    
    export interface OutdatedQuestionsFoundDTO {
      code: string;
    }
    
    export class OutdatedQuestionsFound extends LeoRPCError<RPCError> {
      readonly isOutdatedQuestionsFound: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.OutdatedQuestionsFound;
        super(code);
      }
    
    
      toDTO(): OutdatedQuestionsFoundDTO {
        const dto: OutdatedQuestionsFoundDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    OutdatedQuestionsFound.prototype.toString = function toString(): string {
      return `ManageSurveyQuestionsRPC.OUTDATED_QUESTIONS_FOUND()`;
    };
    
    
    export interface InvalidQuestionOrdersDTO {
      code: string;
    }
    
    export class InvalidQuestionOrders extends LeoRPCError<RPCError> {
      readonly isInvalidQuestionOrders: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidQuestionOrders;
        super(code);
      }
    
    
      toDTO(): InvalidQuestionOrdersDTO {
        const dto: InvalidQuestionOrdersDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidQuestionOrders.prototype.toString = function toString(): string {
      return `ManageSurveyQuestionsRPC.INVALID_QUESTION_ORDERS()`;
    };
    
    
    export interface InvalidRulesFoundDTO {
      code: string;
    }
    
    export class InvalidRulesFound extends LeoRPCError<RPCError> {
      readonly isInvalidRulesFound: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidRulesFound;
        super(code);
      }
    
    
      toDTO(): InvalidRulesFoundDTO {
        const dto: InvalidRulesFoundDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidRulesFound.prototype.toString = function toString(): string {
      return `ManageSurveyQuestionsRPC.INVALID_RULES_FOUND()`;
    };
    

    export type Errors = InvalidSurveyId | InvalidProjectId | SurveyIsClosed | ProjectAlreadyArchived | SurveyIsLive | OutdatedQuestionsFound | InvalidQuestionOrders | InvalidRulesFound;
    export type ErrorsDTO = InvalidSurveyIdDTO | InvalidProjectIdDTO | SurveyIsClosedDTO | ProjectAlreadyArchivedDTO | SurveyIsLiveDTO | OutdatedQuestionsFoundDTO | InvalidQuestionOrdersDTO | InvalidRulesFoundDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidSurveyId:
          return new InvalidSurveyId();
        case RPCError.InvalidProjectId:
          return new InvalidProjectId();
        case RPCError.SurveyIsClosed:
          return new SurveyIsClosed();
        case RPCError.ProjectAlreadyArchived:
          return new ProjectAlreadyArchived();
        case RPCError.SurveyIsLive:
          return new SurveyIsLive();
        case RPCError.OutdatedQuestionsFound:
          return new OutdatedQuestionsFound();
        case RPCError.InvalidQuestionOrders:
          return new InvalidQuestionOrders();
        case RPCError.InvalidRulesFound:
          return new InvalidRulesFound();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestSurveyIdError extends ManageSurveyQuestionsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSurveyIdError";
  }
}

export class InvalidRequestProjectIdError extends ManageSurveyQuestionsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdError";
  }
}

export class InvalidRequestQuestionOrdersError extends ManageSurveyQuestionsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestQuestionOrdersError";
  }
}

export class InvalidRequestQuestionOrdersMinSizeError extends InvalidRequestQuestionOrdersError {
  readonly minSize: number;
  constructor(minSize: number, message?: string) {
    super(message);
    this.minSize = minSize;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestQuestionOrdersMinSizeError";
  }
}

export class InvalidResponseSurveyDetailsAndQuestionsError extends ManageSurveyQuestionsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyDetailsAndQuestionsError";
  }
}

export class InvalidResponseInvalidSurveyIdError extends ManageSurveyQuestionsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidSurveyIdError";
  }
}

export class InvalidResponseInvalidProjectIdError extends ManageSurveyQuestionsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectIdError";
  }
}

export class InvalidResponseSurveyIsClosedError extends ManageSurveyQuestionsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyIsClosedError";
  }
}

export class InvalidResponseProjectAlreadyArchivedError extends ManageSurveyQuestionsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseProjectAlreadyArchivedError";
  }
}

export class InvalidResponseSurveyIsLiveError extends ManageSurveyQuestionsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyIsLiveError";
  }
}

export class InvalidResponseOutdatedQuestionsFoundError extends ManageSurveyQuestionsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseOutdatedQuestionsFoundError";
  }
}

export class InvalidResponseInvalidQuestionOrdersError extends ManageSurveyQuestionsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidQuestionOrdersError";
  }
}

export class InvalidResponseInvalidRulesFoundError extends ManageSurveyQuestionsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidRulesFoundError";
  }
}

